import { SALE_ORDER_ITEM_TYPE } from '@bandido/shared/constants';

import {
  BookingActionState,
  bookingStateLabelKey as resolveBookingStateLabelKey,
  hasActiveOperation as bookingHasActiveOperation,
  isOnRequest,
} from '@frontend/features/travel-services/bookingState';

const RUTERO_SALE_ORDER_ITEM_TYPES = [
  SALE_ORDER_ITEM_TYPE.Rail,
  SALE_ORDER_ITEM_TYPE.Buses,
  SALE_ORDER_ITEM_TYPE.Ferry,
  SALE_ORDER_ITEM_TYPE.Tours,
  SALE_ORDER_ITEM_TYPE.Circuits,
  SALE_ORDER_ITEM_TYPE.Transfers,
  SALE_ORDER_ITEM_TYPE.Cars,
  SALE_ORDER_ITEM_TYPE.Esims,
] as const;

type RuteroSaleOrderItemType = (typeof RUTERO_SALE_ORDER_ITEM_TYPES)[number];

export const RUTERO_SALE_ORDER_BOOKING_STATE_COLORS: Record<
  string,
  'default' | 'info' | 'success' | 'error'
> = {
  draft: 'default',
  quoted: 'info',
  held: 'info',
  confirmed: 'success',
  cancelled: 'error',
  failed: 'error',
};

export interface RuteroSaleOrderSummaryAuthorityEntry<
  TBooking extends BookingActionState & {
    providerStatus?: string;
    expiresAt?: string;
  } = BookingActionState & {
    providerStatus?: string;
    expiresAt?: string;
  },
> {
  booking?: TBooking;
  isLoading: boolean;
  isError: boolean;
}

export function isRuteroSaleOrderItemType(itemType: string): boolean {
  return RUTERO_SALE_ORDER_ITEM_TYPES.includes(
    itemType as RuteroSaleOrderItemType,
  );
}

export function getRuteroSaleOrderSummaryPresentation<
  TBooking extends BookingActionState & {
    providerStatus?: string;
    expiresAt?: string;
  },
>(
  entry: RuteroSaleOrderSummaryAuthorityEntry<TBooking>,
):
  | { kind: 'loading'; messageKey: string }
  | { kind: 'unavailable'; messageKey: string }
  | {
      kind: 'booking';
      booking: TBooking;
      bookingStateLabelKey: string;
      showUnknownProviderWarning: boolean;
      showActiveOperation: boolean;
      showProviderStatus: boolean;
      showHoldExpiry: boolean;
    } {
  if (entry.isLoading && !entry.booking) {
    return {
      kind: 'loading',
      messageKey: 'cart.ruteroAuthority.rowVerifying',
    };
  }

  if (entry.isError || !entry.booking) {
    return {
      kind: 'unavailable',
      messageKey: 'cart.ruteroAuthority.rowUnavailable',
    };
  }

  return {
    kind: 'booking',
    booking: entry.booking,
    bookingStateLabelKey: resolveBookingStateLabelKey(
      entry.booking.state,
      entry.booking.providerStatus,
    ),
    showUnknownProviderWarning: entry.booking.unknownProviderState === true,
    showActiveOperation: bookingHasActiveOperation(entry.booking),
    showProviderStatus:
      Boolean(entry.booking.providerStatus) &&
      !isOnRequest(entry.booking.providerStatus),
    showHoldExpiry:
      entry.booking.state === 'held' && Boolean(entry.booking.expiresAt),
  };
}
